import { useRef, useState } from 'react';

import * as styles from '../FirstResponder.module.css';
import { LiveStopCopy } from './liveViewModel';
import { useModalA11y } from './useModalA11y';

/*
 * Stop confirmation. The copy is TRUTHFUL for LIVE semantics: work can be
 * in flight, watches are exited (stopped_by_operator) and are not revived by
 * a restart. The prototype's "nothing is queued, so there is nothing to
 * drain" sentence is deliberately absent — it is false for LIVE.
 *
 * GRAVY-10373 Task 3: Stop now goes through the same real mode endpoint as every other
 * transition (POST .../mode with { mode: 'off', reason }), which requires a non-blank reason —
 * so this modal collects one, using the same `!reason.trim()` rule as ConnectedPolicyEditor and
 * ModeReasonModal. `pending` disables the confirm button outright while the request is in
 * flight, same as the mode buttons in ConsoleHeader — never a double-submit.
 */

type Props = {
  copy: LiveStopCopy;
  pending: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
};

const StopConfirmModal = ({ copy, pending, onConfirm, onCancel }: Props) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y(true, onCancel, dialogRef);
  const [reason, setReason] = useState('');

  return (
    <div
      className={styles.overlay}
      data-testid="flp-stop-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className={`${styles.modal} ${styles.modalSm}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="flp-stop-title"
        ref={dialogRef}
        tabIndex={-1}
        data-testid="flp-stop-modal"
      >
        <div className={styles.modalHeader}>
          <h3 id="flp-stop-title">{copy.title}</h3>
          <button
            type="button"
            className={styles.x}
            aria-label="Close"
            onClick={onCancel}
          >
            ×
          </button>
        </div>
        <div className={styles.section}>
          <p style={{ margin: '0 0 8px' }}>{copy.body}</p>
          <p
            className={`${styles.muted} ${styles.small}`}
            style={{ margin: '0 0 8px' }}
          >
            {copy.inflight}
          </p>
          <p
            className={`${styles.muted} ${styles.small}`}
            style={{ margin: '0 0 12px' }}
          >
            {copy.sub}
          </p>
          <div className={styles.k}>Reason (required)</div>
          <input
            aria-label="Stop reason"
            data-testid="flp-stop-reason"
            className={styles.input}
            style={{ width: '100%', marginTop: 6 }}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <div
          className={styles.section}
          style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}
        >
          <button
            type="button"
            className={`${styles.btn} ${styles.btnOutline}`}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnDanger}`}
            data-testid="flp-stop-confirm"
            disabled={!reason.trim() || pending}
            onClick={() => onConfirm(reason.trim())}
          >
            Stop First Responder
          </button>
        </div>
      </div>
    </div>
  );
};

export default StopConfirmModal;
